<script>
    import { select } from 'd3-selection';
    import { schemeCategory10 } from 'd3-scale-chromatic';
    import { scaleLinear, scaleOrdinal } from 'd3-scale';
    import { axisBottom, axisLeft } from 'd3-axis';
    import { bin, mean, max, extent } from 'd3-array';
    import { line, curveBasis } from 'd3-shape';
    import { getContext, onMount } from 'svelte';

    // --- Properties
    const width = 150;
    const height = 350;
    const padding = 6;
    const facetHeight = (height - 2 * padding) / 3;
    const axisPadding = 26;

    export let dataKey;
    const { carStops, focus } = getContext(dataKey);

    // --- Traces
    $: trace = computeTraces($focus.poi);

    function computeTraces(point) {
        if (!point) {
            return {
                arrivalBins: [],
                departureBins: [],
                durationBins: [],
                business: [],
            };
        }
        const visits = point.stops.map((i) => $carStops[i]);

        const arrivalBins = timeBinner
            .value((d) => d.start.time_of_day)(visits)
            .map((bin) => {
                const { x0, x1 } = bin;
                return { x0, x1, value: bin.length / 14 };
            });
        const departureBins = timeBinner
            .value((d) => d.stop.time_of_day)(visits)
            .map((bin) => {
                const { x0, x1 } = bin;
                return { x0, x1, value: bin.length / 14 };
            });
        const durationBins = durationBinner
            .value((d) => d.duration)(visits)
            .map((bin) => {
                const { x0, x1 } = bin;
                return { x0, x1, value: bin.length / 14 };
            });

        // Create sorted array of arrival & departure events
        const events = visits
            .reduce((arr, v) => {
                arr.push({ time: v.start.time_of_day, change: +1 });
                arr.push({ time: v.stop.time_of_day, change: -1 });
                return arr;
            }, [])
            .sort((a, b) => a.time - b.time);
        // Compute cummulative sum to get the number of visitors at those
        // change points
        const visitors = events.reduce((arr, v, i) => {
            const value = i == 0 ? v.change : arr[i - 1].value + v.change;
            arr.push({ time: v.time, value });
            return arr;
        }, []);
        // Compute the average number of visitors in half hour segments.
        // Fill in periods without change with the previous value
        let takeAlongValue = 0;
        const visitorsBin = timeBinner
            .value((d) => d.time)(visitors)
            .map((bin) => {
                const { x0, x1 } = bin;
                const value =
                    bin.length > 0 ? mean(bin, (v) => v.value) : takeAlongValue;
                takeAlongValue = value;
                return { x0, x1, value };
            });
        // Scale the number of visitors to lie in [0-1] because integration
        // is up to an unknown constant...
        const businessScale = scaleLinear().domain(
            extent(visitorsBin, (d) => d.value)
        );
        const business = visitorsBin.map((v) => {
            v.value = businessScale(v.value);
            return v;
        });

        return {
            arrivalBins,
            departureBins,
            durationBins,
            business,
        };
    }

    // --- Color Scales
    const variables = ['business', 'arrivals', 'departures', 'duration'];
    const color = scaleOrdinal(schemeCategory10).domain(variables);

    // --- Time Scale
    const timeScale = scaleLinear()
        .range([padding + axisPadding, width - padding])
        .domain([0, 24 * 3600]);
    const timeThresholds = Array.from({ length: 48 }, (v, i) => (3600 / 2) * i);
    const timeBinner = bin()
        .domain(timeScale.domain())
        .thresholds(timeThresholds);

    const timeAxesG = [null, null];
    const tickValuesT = [0, 6 * 3600, 12 * 3600, 18 * 3600];
    const tickLabelsT = ['00:00', '06:00', '12:00', '18:00'];
    const timeAxis = axisBottom(timeScale)
        .tickValues(tickValuesT)
        .tickFormat((d, i) => tickLabelsT[i]);

    // --- Duration Scale
    const durationScale = scaleLinear()
        .range([padding + axisPadding, width - padding])
        .domain([0, 12 * 3600]);
    const durationThresholds = Array.from(
        { length: 48 },
        (v, i) => (3600 / 4) * i
    );
    const durationBinner = bin()
        .domain(durationScale.domain())
        .thresholds(durationThresholds);

    let durationAxisG = {};
    const tickValuesD = [1 * 3600, 4 * 3600, 8 * 3600, 12 * 3600];
    const durationAxis = axisBottom(durationScale)
        .tickValues(tickValuesD)
        .tickFormat((d, i) => d / 3600);

    // --- Density Scale
    const densityScale = scaleLinear()
        .range([facetHeight - axisPadding - padding, padding])
        .domain([0, 2]);

    let densityAxisG1;
    let densityAxisG2;
    const densityAxis = axisLeft(densityScale).ticks(5);

    // --- Business Scale
    const businessScale = scaleLinear()
        .range([facetHeight - axisPadding - padding, padding])
        .domain([0, 1]);

    let businessAxisG = {};
    const businessAxis = axisLeft(businessScale).ticks(5);
    const businessLine = line()
        .curve(curveBasis)
        .x((t) => timeScale((t.x0 + t.x1) / 2))
        .y((t) => businessScale(t.value));

    // --- Mount axes
    onMount(() => {
        timeAxesG.forEach((g) => {
            select(g)
                .call(timeAxis)
                .call((g) => g.select('.domain').remove());
        });

        select(durationAxisG)
            .call(durationAxis)
            .call((g) => g.select('.domain').remove());

        select(businessAxisG)
            .call(businessAxis)
            .call((g) => g.select('.domain').remove());
    });

    // --- Flexible axis
    $: updateDensityAxis(densityAxisG1, densityAxisG2, trace);
    function updateDensityAxis(g1, g2, traces) {
        if (traces.arrivalBins.length == 0) {
            densityScale.domain([0, 2]);
        } else {
            const arMax = max(traces.arrivalBins, (d) => d.value);
            const deMax = max(traces.departureBins, (d) => d.value);
            const duMax = max(traces.durationBins, (d) => d.value);
            const upper = max([arMax, deMax, duMax]);
            densityScale.domain([0, upper + 2]);
        }

        [g1, g2].forEach((g) => {
            if (g) {
                select(g)
                    .call(densityAxis)
                    .call((g) => g.select('.domain').remove());
            }
        });
    }
</script>

<div id="traces" class="col" style="width:{width}px;height:{height}px;">
    <svg {width} {height} overflow="auto">
        <g transform="translate(0, 0)">
            <g>
                <path
                    d={businessLine(trace.business)}
                    fill="transparent"
                    stroke={color('business')}
                />
            </g>
            <g
                bind:this={businessAxisG}
                transform="translate({axisPadding + padding}, 0)"
            >
                <g
                    transform="translate({-axisPadding +
                        1}, {axisPadding}) rotate(-90)"
                >
                    <text fill="currentColor" dx="-0.71em">busyness</text>
                </g>
            </g>
            <g
                bind:this={timeAxesG[0]}
                transform="translate(0, {facetHeight - axisPadding})"
            >
                <g transform="translate({width / 2}, 18)">
                    <text fill="currentColor" dy="0.71em">time of day</text>
                </g>
            </g>
        </g>
        <g transform="translate(0, {facetHeight + (2 * padding) / 2})">
            <g>
                {#each trace.arrivalBins as arr}
                    <rect
                        fill={color('arrivals')}
                        x={timeScale(arr.x0) + 1}
                        y={densityScale(arr.value)}
                        width={timeScale(arr.x1) - timeScale(arr.x0) - 1}
                        height={densityScale(0) - densityScale(arr.value)}
                        opacity="0.7"
                    />
                {/each}
            </g>
            <g>
                {#each trace.departureBins as arr}
                    <rect
                        fill={color('departures')}
                        x={timeScale(arr.x0) + 1}
                        y={densityScale(arr.value)}
                        width={timeScale(arr.x1) - timeScale(arr.x0) - 1}
                        height={densityScale(0) - densityScale(arr.value)}
                        opacity="0.7"
                    />
                {/each}
            </g>
            <g
                font-size="10"
                font-family="sans-serif"
                text-anchor="begin"
                transform="translate({width - 60}, 10)"
            >
                <rect
                    fill={color('arrivals')}
                    x="0"
                    y="0"
                    width="5"
                    height="5"
                />
                <text x="7" y="5" fill="currentColor"> arrivals </text>
                <rect
                    fill={color('departures')}
                    x="0"
                    y="7"
                    width="5"
                    height="5"
                />
                <text x="7" y="13" fill="currentColor"> departures </text>
            </g>
            <g
                bind:this={densityAxisG1}
                transform="translate({axisPadding + padding}, 0)"
            >
                <g
                    transform="translate({-axisPadding}, {axisPadding}) rotate(-90)"
                >
                    <text fill="currentColor" dx="-0.71em">avg count</text>
                </g>
            </g>
            <g
                bind:this={timeAxesG[1]}
                transform="translate(0, {facetHeight - axisPadding})"
            >
                <g transform="translate({width / 2}, 18)">
                    <text fill="currentColor" dy="0.71em">time of day</text>
                </g>
            </g>
        </g>
        <g transform="translate(0, {2 * facetHeight + (3 * padding) / 2})">
            <g>
                {#each trace.durationBins as arr}
                    <rect
                        fill={color('duration')}
                        x={durationScale(arr.x0) + 1}
                        y={densityScale(arr.value)}
                        width={durationScale(arr.x1) -
                            durationScale(arr.x0) -
                            1}
                        height={densityScale(0) - densityScale(arr.value)}
                    />
                {/each}
            </g>
            <g
                font-size="10"
                font-family="sans-serif"
                text-anchor="begin"
                transform="translate({width - 60}, 10)"
            >
                <rect
                    fill={color('duration')}
                    x="0"
                    y="0"
                    width="5"
                    height="5"
                />
                <text x="7" y="5" fill="currentColor"> visits </text>
            </g>
            <g
                bind:this={densityAxisG2}
                transform="translate({axisPadding + padding}, 0)"
            >
                <g
                    transform="translate({-axisPadding}, {axisPadding}) rotate(-90)"
                >
                    <text fill="currentColor" dx="-0.71em">avg count</text>
                </g>
            </g>
            <g
                bind:this={durationAxisG}
                transform="translate(0, {facetHeight - axisPadding})"
            >
                <g transform="translate({width / 2}, 18)">
                    <text fill="currentColor" dy="0.71em">duration (hour)</text>
                </g>
            </g>
        </g>
    </svg>
</div>

<style>
</style>
